// V11.2 — Résolution unifiée des photos avec fallback intelligent.
//
// Règle : la vraie photo si présente, sinon un asset fallback officiel.
// Le fallback n'est JAMAIS stocké en base — il est résolu dynamiquement à
// l'affichage. Cela évite qu'une "fausse photo" ne pollue le champ
// photo_url / logo_url en base et facilite le passage automatique à la vraie
// photo dès qu'elle est chargée.
//
// V11.11 — Ajout du fallback dédié Coach / Encadrant (silhouette avec sifflet).

const FALLBACKS = {
  athlete_homme:   "/branding/athlete-male.png",
  athlete_femme:   "/branding/athlete-female.png",
  athlete_neutre:  "/branding/athlete-male.png", // neutre par défaut sur silhouette homme
  coach:           "/branding/coach-default.png",
  club:            "/branding/club-default.png",
  federation:      "/branding/federation-default.png",
  // V11.13 — Bannières (cover_url) de secours pour les fiches détail
  cover_athlete:    "/branding/cover-athlete-default.png",
  cover_coach:      "/branding/cover-coach-default.png",
  cover_club:       "/branding/cover-club-default.png",
  cover_federation: "/branding/cover-federation-default.png",
};

// V13.4 — Base URL pour préfixer les public_url relatifs des médias uploadés.
const BACKEND_BASE = (typeof process !== "undefined" && process.env && process.env.REACT_APP_BACKEND_URL) || "";

function _abs(u) {
  if (!u) return u;
  return u.startsWith("http") ? u : `${BACKEND_BASE}${u}`;
}

function _slotUrl(entity, slot) {
  const files = entity?.media_files;
  if (!Array.isArray(files)) return null;
  for (const f of files) {
    if (!f || typeof f !== "object") continue;
    if (f.slot === slot && f.public_url) return _abs(String(f.public_url).trim());
  }
  return null;
}

function _clean(u) {
  if (!u) return null;
  const s = String(u).trim();
  if (!s || s === "null" || s === "undefined") return null;
  return s;
}

/** Détermine si un user/athlete a un rôle coach/encadrant. */
function _isCoach(entity) {
  if (!entity) return false;
  const roles = entity.roles || entity.role_list || [];
  if (Array.isArray(roles)) {
    const rs = roles.map((r) => String(r).toLowerCase());
    if (rs.some((r) => ["coach", "entraineur", "entraîneur", "encadrant", "staff", "official"].includes(r))) {
      return true;
    }
  }
  const single = String(entity.role || entity.profile_type || entity.kind || "").toLowerCase();
  if (["coach", "entraineur", "entraîneur", "encadrant", "staff", "official"].includes(single)) return true;
  // Champ booléen explicite
  if (entity.is_coach === true) return true;
  return false;
}

/**
 * Retourne l'URL de la photo Athlete à afficher.
 * V13.4 — Priorité :
 *   1. media_files[slot=profile]  (upload direct AITO.PF)
 *   2. photo_url                  (URL externe/secondaire)
 *   3. fallback selon sexe (jamais persisté en base)
 * @param {{photo_url?:string, media_files?:object[], sex?:string, roles?:string[], role?:string, is_coach?:boolean}} a
 */
export function resolveAthletePhoto(a) {
  const uploaded = _slotUrl(a, "profile");
  if (uploaded) return uploaded;
  const real = _clean(a && a.photo_url);
  if (real) return real;
  // V11.11 — Si l'entité est marquée coach/encadrant, fallback dédié.
  if (_isCoach(a)) return FALLBACKS.coach;
  const sex = ((a && a.sex) || "").toLowerCase();
  if (sex === "homme" || sex === "male" || sex === "m") return FALLBACKS.athlete_homme;
  if (sex === "femme" || sex === "female" || sex === "f") return FALLBACKS.athlete_femme;
  return FALLBACKS.athlete_neutre;
}

/**
 * V13.4 — Retourne l'URL de la bannière Athlete à afficher.
 * Priorité : media_files[slot=banner] → banner_url → fallback cover_athlete.
 */
export function resolveAthleteBanner(a) {
  const uploaded = _slotUrl(a, "banner");
  if (uploaded) return uploaded;
  const real = _clean(a && a.banner_url);
  if (real) return real;
  return FALLBACKS.cover_athlete;
}

/**
 * V11.11 — Retourne l'URL de la photo pour un utilisateur / coach / encadrant.
 * Diffère de resolveAthletePhoto : on privilégie le fallback coach dès qu'aucune photo
 * réelle n'est fournie, indépendamment du sexe.
 * @param {{photo_url?:string, avatar_url?:string, roles?:string[], role?:string, sex?:string}} u
 */
export function resolveCoachPhoto(u) {
  const real = _clean(u && (u.photo_url || u.avatar_url));
  if (real) return real;
  return FALLBACKS.coach;
}

/**
 * V11.11 — Résolveur universel : choisit le bon fallback selon le contexte
 * ("athlete" | "coach" | "club" | "federation").
 */
export function resolveEntityPhoto(entity, kind = "athlete") {
  const k = String(kind || "").toLowerCase();
  if (k === "coach" || k === "encadrant" || k === "staff") return resolveCoachPhoto(entity);
  if (k === "club") return resolveClubLogo(entity);
  if (k === "federation" || k === "fed") return resolveFederationLogo(entity);
  return resolveAthletePhoto(entity);
}

/**
 * Retourne l'URL du logo Club à afficher.
 * @param {{logo_url?:string, photo_url?:string}} c
 */
export function resolveClubLogo(c) {
  return _clean(c && (c.logo_url || c.photo_url)) || FALLBACKS.club;
}

/**
 * Retourne l'URL du logo Fédération à afficher.
 * @param {{logo_url?:string, photo_url?:string}} f
 */
export function resolveFederationLogo(f) {
  return _clean(f && (f.logo_url || f.photo_url)) || FALLBACKS.federation;
}

/**
 * V11.13 — Retourne l'URL de la BANNIÈRE (cover) selon le type d'entité.
 * Utilisée dans les headers des pages détail (Athlete/Coach/Club/Federation).
 *
 * @param {object|null} entity   L'entité (peut être null)
 * @param {"athlete"|"coach"|"club"|"federation"} kind
 * @returns {string}   URL de la cover_url réelle si présente, sinon fallback dédié.
 */
export function resolveEntityCover(entity, kind = "athlete") {
  // V13.4 — Priorité : banner uploadée (slot=banner) → cover_url → banner_url → fallback
  const uploaded = _slotUrl(entity, "banner");
  if (uploaded) return uploaded;
  const real = _clean(entity && (entity.cover_url || entity.banner_url));
  if (real) return real;
  const k = String(kind || "").toLowerCase();
  if (k === "coach" || k === "encadrant" || k === "staff") return FALLBACKS.cover_coach;
  if (k === "club") return FALLBACKS.cover_club;
  if (k === "federation" || k === "fed") return FALLBACKS.cover_federation;
  return FALLBACKS.cover_athlete;
}

export { FALLBACKS };
